import { useEffect, useState, useRef, useMemo } from 'react';
import { Player } from '@lordicon/react';
import { useTranslator } from '@front-activerse/common-components';
import { Container, Row, Col, Form, Input, InputGroupText, Button } from 'reactstrap';
import Layout from '../../../Layout/Layout';
import TitleBar from '../../../Layout/TitleBar';
import { useAppContext, Loader } from '@front-activerse/common-components';
import { useParams, useLocation, useOutletContext } from 'react-router-dom';
import DayPlanMealReplacement from './DayPlanMealReplacement';

const DayPlanMealReplacementContainer = ({
  loadDayPlanMealReplacements,
  replaceMeal,
  insertFavoriteToDayplan,
}) => {
  const { trackFirebaseEvent } = useOutletContext();
  const { date, dietMealId, mealType, userDietSetId, originalMealId } = useParams();
  const { state } = useLocation();
  const { t } = useTranslator();

  const {
    images: { noSearchResultsIcon, searchButton },
    lotties: { searchIcon },
  } = useAppContext();

  const playerRefSearch = useRef(null);
  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();
    return color || '#000000';
  }, []);

  const [replacements, setReplacements] = useState(state.replacements);
  const [search, setSearch] = useState('');
  const [useSearchResult, setUseSearchResult] = useState(false);
  const [preload, setPreload] = useState(false);

  useEffect(() => {
    trackFirebaseEvent('screen_view ', {
      firebase_screen: 'Change_meal ',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMealReplacements = async (text) => {
    setPreload(true);

    const request = {
      userDietSetId: +userDietSetId,
      mealType: +mealType,
      dietMealId: +dietMealId,
      limit: 10,
      prependWithOriginalMeal: !!originalMealId && search === '',
      text,
    };
    const result = await loadDayPlanMealReplacements.load(request);

    setPreload(false);

    if (result.data.me.dailyDietMeal) {
      setReplacements(result.data.me.dailyDietMeal.replacements);
      setUseSearchResult(true);
    }
  };

  const handleChange = (event) => {
    setSearch(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    loadMealReplacements(search);
  };

  const rightReplacements = useSearchResult ? replacements : state.replacements;

  return (
    <Layout>
      <TitleBar level={2}>{t('mealPlan.changeMeal.navbarTitle')}</TitleBar>

      <div className="page-template-meal-plan">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <div className="search-container">
                  <Form onSubmit={handleSubmit}>
                    <div
                      className="input-group-with-addon start"
                      onMouseEnter={() => playerRefSearch.current?.playFromBeginning()}
                      onTouchStart={() => playerRefSearch.current?.playFromBeginning()}
                    >
                      <InputGroupText>
                        <Player
                          ref={playerRefSearch}
                          icon={searchIcon}
                          size={24}
                          colorize={iconsIconMiddleColor}
                        />
                      </InputGroupText>
                      <Input
                        name="search"
                        type="search"
                        enterkeyhint="search"
                        value={search}
                        onChange={handleChange}
                        placeholder={t('meal.search.inputPlaceholder')}
                      />
                      <Button type="submit" color="primary" size="sm" className="ms-2 px-m">
                        <img src={searchButton} alt="search" />
                      </Button>
                    </div>
                  </Form>
                </div>

                <div className="search-result-container">
                  {(useSearchResult || (rightReplacements && rightReplacements.length > 0)) && (
                    <h2 className="title-medium semi-bold mb-m">
                      {useSearchResult
                        ? t('meal.searchResults.title')
                        : t('meal.recommendations.title')}
                    </h2>
                  )}

                  {rightReplacements && rightReplacements.length > 0 ? (
                    rightReplacements.map((mealReplacement) => (
                      <DayPlanMealReplacement
                        key={mealReplacement.id}
                        mealId={+dietMealId}
                        originalMealId={+originalMealId}
                        mealReplacement={mealReplacement}
                        replaceMeal={replaceMeal}
                        insertFavoriteToDayplan={insertFavoriteToDayplan}
                        userDietSetId={+userDietSetId}
                        mealType={+mealType}
                        date={date}
                        from={state.from}
                      />
                    ))
                  ) : !useSearchResult ? (
                    <div className="text-center pt-xl">
                      <p className="text-text-variant-color mb-m">
                        {t('mealPlan.changeMeal.noReplacements')}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <img
                        src={noSearchResultsIcon}
                        width={64}
                        height={64}
                        className="mb-m"
                        alt=""
                      />
                      <p className="text-text-variant-color mb-m">
                        {t('meal.searchResults.noResultsContent')}
                      </p>
                      <p className="text-text-variant-color">
                        {t('meal.searchResults.noResultsAdditionalContent')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {preload ? <Loader /> : ''}
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

export default DayPlanMealReplacementContainer;
